import { useMemo } from 'react';
import { Box, Card, CardContent, CardHeader, Chip, Skeleton, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ReactApexChart from 'react-apexcharts';
import { reportColors, styleReportChart } from '../../views/shared/report-design';

const number = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};
const percent = (value) => {
    const parsed = number(value);
    return Math.max(0, Math.min(100, parsed <= 1 ? parsed * 100 : parsed));
};
const formatNumber = (value) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(number(value));
const formatCurrency = (value) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(number(value));
const formatPercent = (value) => `${number(value).toFixed(1)}%`;
const labelFor = (row, keys) => keys.map((key) => row?.[key]).find((value) => String(value || '').trim()) || '(not set)';

export const buildGa4ShareData = (rows, labelKeys, valueKeys, limit = 4) => {
    const ranked = (Array.isArray(rows) ? rows : [])
        .map((row) => ({ label: String(labelFor(row, labelKeys)), value: valueKeys.map((key) => row?.[key]).find((value) => value != null) }))
        .map((row) => ({ ...row, value: number(row.value) }))
        .filter((row) => row.value > 0)
        .sort((a, b) => b.value - a.value);
    const leading = ranked.slice(0, limit);
    const other = ranked.slice(limit).reduce((sum, row) => sum + row.value, 0);
    if (other > 0) leading.push({ label: 'Other', value: other });
    return { labels: leading.map((row) => row.label), series: leading.map((row) => row.value), rows: leading };
};

const EmptyChart = ({ message }) => (
    <Box sx={{ minHeight: 260, display: 'grid', placeItems: 'center', textAlign: 'center', px: 2 }}>
        <Typography variant="body2" color="text.secondary">{message}</Typography>
    </Box>
);

const InsightGrid = ({ children }) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
        {children}
    </Box>
);

const useChartBase = () => {
    const theme = useTheme();
    const options = useMemo(() => styleReportChart(theme, {
        chart: {
            type: 'bar',
            toolbar: { show: false },
            zoom: { enabled: false },
            fontFamily: theme.typography.fontFamily,
        },
        colors: reportColors.categories,
        dataLabels: { enabled: false },
        grid: { borderColor: alpha(theme.palette.text.primary, 0.1) },
        legend: { position: 'bottom', fontFamily: theme.typography.fontFamily },
        tooltip: { theme: theme.palette.mode },
    }), [theme]);
    return { options, theme };
};

const ShareChart = ({ title, subtitle, data, loading, cardSx, valueFormat = 'number' }) => {
    const { options: base, theme } = useChartBase();
    const total = data.series.reduce((sum, value) => sum + value, 0);
    const options = useMemo(() => ({
        ...base,
        chart: { ...base.chart, type: 'donut' },
        labels: data.labels,
        stroke: { colors: [theme.palette.background.paper], width: 2 },
        plotOptions: {
            pie: {
                expandOnClick: true,
                donut: {
                    size: '66%',
                    labels: {
                        show: true,
                        total: { show: true, label: 'Total', formatter: () => valueFormat === 'currency' ? formatCurrency(total) : formatNumber(total) },
                        value: { formatter: (value) => valueFormat === 'currency' ? formatCurrency(value) : formatNumber(value) },
                    },
                },
            },
        },
        tooltip: { ...base.tooltip, y: { formatter: (value) => valueFormat === 'currency' ? formatCurrency(value) : formatNumber(value) } },
    }), [base, data.labels, theme, total, valueFormat]);

    return (
        <Card variant="outlined" sx={cardSx}>
            <CardHeader titleTypographyProps={{ component: 'h2' }} title={title} subheader={subtitle} />
            <CardContent>
                {loading ? <Skeleton variant="rounded" height={280} /> : data.series.length ? (
                    <Box role="img" aria-label={`${title}. ${data.rows.map((row) => `${row.label}: ${valueFormat === 'currency' ? formatCurrency(row.value) : formatNumber(row.value)}`).join(', ')}.`}>
                        <ReactApexChart options={options} series={data.series} type="donut" height={280} />
                    </Box>
                ) : <EmptyChart message={`No ${title.toLowerCase()} data is available for this range.`} />}
            </CardContent>
        </Card>
    );
};

export const AudienceQualityVisual = ({ kpis = {}, conversionSummary = {}, loading = false, cardSx = {} }) => {
    const { options: base, theme } = useChartBase();
    const users = number(kpis.users);
    const sessions = number(kpis.sessions);
    const series = useMemo(() => [
        percent(kpis.engagementRate),
        users ? Math.min((number(kpis.newUsers) / users) * 100, 100) : 0,
        percent(conversionSummary.conversionRate || (sessions ? (number(kpis.conversions) / sessions) * 100 : 0)),
    ], [conversionSummary.conversionRate, kpis.conversions, kpis.engagementRate, kpis.newUsers, sessions, users]);
    const labels = useMemo(() => ['Engagement rate', 'New-user share', 'Conversion rate'], []);
    const options = useMemo(() => ({
        ...base,
        chart: { ...base.chart, type: 'radialBar' },
        labels,
        plotOptions: {
            radialBar: {
                startAngle: -90,
                endAngle: 270,
                hollow: { size: '34%' },
                track: { background: alpha(theme.palette.text.primary, 0.08) },
                dataLabels: { name: { fontSize: '13px' }, value: { fontSize: '18px', fontWeight: 800, formatter: formatPercent }, total: { show: true, label: 'Audience quality', formatter: () => formatPercent(series.reduce((sum, value) => sum + value, 0) / series.length) } },
            },
        },
        stroke: { lineCap: 'round' },
        legend: { ...base.legend, show: true },
    }), [base, labels, series, theme]);

    return (
        <Card variant="outlined" sx={cardSx}>
            <CardHeader titleTypographyProps={{ component: 'h2' }} title="Audience Quality Pulse" subheader="Engagement, acquisition freshness, and conversion efficiency in one view" />
            <CardContent sx={{ display: 'grid', gap: 1 }}>
                {loading ? <Skeleton variant="rounded" height={300} /> : (
                    <Box role="img" aria-label={labels.map((label, index) => `${label}: ${formatPercent(series[index])}`).join(', ')}>
                        <ReactApexChart options={options} series={series} type="radialBar" height={300} />
                    </Box>
                )}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap">
                    {labels.map((label, index) => <Chip key={label} label={`${label}: ${formatPercent(series[index])}`} variant="outlined" sx={{ fontWeight: 700 }} />)}
                </Stack>
            </CardContent>
        </Card>
    );
};

export const AcquisitionInsightVisuals = ({ sources = [], loading = false, cardSx = {} }) => {
    const { options: base } = useChartBase();
    const mix = useMemo(() => buildGa4ShareData(sources, ['sourceMedium', 'source', 'medium'], ['sessions']), [sources]);
    const efficiencyRows = useMemo(() => (Array.isArray(sources) ? sources : [])
        .filter((row) => number(row.sessions) > 0)
        .sort((a, b) => number(b.sessions) - number(a.sessions))
        .slice(0, 10), [sources]);
    const efficiencySeries = efficiencyRows.map((row) => ({
        name: String(labelFor(row, ['sourceMedium', 'source', 'medium'])),
        data: [{ x: number(row.sessions), y: number(row.conversions), z: Math.max(number(row.newUsers || row.users), 1) }],
    }));
    const bubbleOptions = useMemo(() => ({
        ...base,
        chart: { ...base.chart, type: 'bubble' },
        xaxis: { title: { text: 'Sessions' }, labels: { formatter: formatNumber } },
        yaxis: { title: { text: 'Conversions' }, min: 0, labels: { formatter: formatNumber } },
        fill: { type: 'solid', opacity: 1 },
        tooltip: { ...base.tooltip, x: { formatter: (value) => `${formatNumber(value)} sessions` }, y: { formatter: (value) => `${formatNumber(value)} conversions` }, z: { formatter: (value) => `${formatNumber(value)} users` } },
    }), [base]);

    return (
        <InsightGrid>
            <ShareChart title="Acquisition Mix" subtitle="Share of sessions by source / medium" data={mix} loading={loading} cardSx={cardSx} />
            <Card variant="outlined" sx={cardSx}>
                <CardHeader titleTypographyProps={{ component: 'h2' }} title="Source Efficiency" subheader="Sessions drive position, conversions drive height, and bubble size represents users" />
                <CardContent>
                    {loading ? <Skeleton variant="rounded" height={280} /> : efficiencySeries.length ? (
                        <Box role="img" aria-label={`Source efficiency. ${efficiencyRows.map((row) => `${labelFor(row, ['sourceMedium', 'source'])}: ${formatNumber(row.sessions)} sessions and ${formatNumber(row.conversions)} conversions`).join(', ')}.`}>
                            <ReactApexChart options={bubbleOptions} series={efficiencySeries} type="bubble" height={280} />
                        </Box>
                    ) : <EmptyChart message="No source efficiency data is available for this range." />}
                </CardContent>
            </Card>
        </InsightGrid>
    );
};

export const ConversionInsightVisuals = ({ events = [], loading = false, cardSx = {} }) => {
    const { options: base } = useChartBase();
    const mix = useMemo(() => buildGa4ShareData(events, ['name', 'event'], ['conversions']), [events]);
    const revenueRows = useMemo(() => (Array.isArray(events) ? events : []).filter((row) => number(row.revenue) > 0).sort((a, b) => number(b.revenue) - number(a.revenue)).slice(0, 8), [events]);
    const rateRows = useMemo(() => (Array.isArray(events) ? events : []).filter((row) => number(row.conversionRate) > 0).sort((a, b) => number(b.conversionRate) - number(a.conversionRate)).slice(0, 8), [events]);
    const rankedRows = revenueRows.length ? revenueRows : rateRows;
    const usingRevenue = revenueRows.length > 0;
    const barOptions = useMemo(() => ({
        ...base,
        chart: { ...base.chart, type: 'bar' },
        plotOptions: { bar: { horizontal: true, borderRadius: 5, barHeight: '62%', distributed: false } },
        xaxis: { categories: rankedRows.map((row) => String(labelFor(row, ['name', 'event']))), labels: { formatter: usingRevenue ? formatCurrency : formatPercent } },
        tooltip: { ...base.tooltip, y: { formatter: usingRevenue ? formatCurrency : formatPercent } },
    }), [base, rankedRows, usingRevenue]);
    const barSeries = [{ name: usingRevenue ? 'Revenue' : 'Conversion rate', data: rankedRows.map((row) => usingRevenue ? number(row.revenue) : percent(row.conversionRate)) }];

    return (
        <InsightGrid>
            <ShareChart title="Conversion Mix" subtitle="Which key events account for conversion volume" data={mix} loading={loading} cardSx={cardSx} />
            <Card variant="outlined" sx={cardSx}>
                <CardHeader titleTypographyProps={{ component: 'h2' }} title={usingRevenue ? 'Value by Conversion Event' : 'Conversion Event Efficiency'} subheader={usingRevenue ? 'Revenue attributed to each key event' : 'Conversion rate by key event when event revenue is unavailable'} />
                <CardContent>
                    {loading ? <Skeleton variant="rounded" height={280} /> : rankedRows.length ? (
                        <Box role="img" aria-label={`${usingRevenue ? 'Revenue' : 'Conversion rate'} by conversion event. ${rankedRows.map((row) => `${labelFor(row, ['name', 'event'])}: ${usingRevenue ? formatCurrency(row.revenue) : formatPercent(percent(row.conversionRate))}`).join(', ')}.`}>
                            <ReactApexChart options={barOptions} series={barSeries} type="bar" height={280} />
                        </Box>
                    ) : <EmptyChart message="No conversion event comparison data is available for this range." />}
                </CardContent>
            </Card>
        </InsightGrid>
    );
};

export const EcommerceInsightVisuals = ({ products = [], categories = [], loading = false, cardSx = {} }) => {
    const { options: base } = useChartBase();
    const mix = useMemo(() => buildGa4ShareData(categories, ['name', 'category'], ['revenue'], 4), [categories]);
    const productRows = useMemo(() => (Array.isArray(products) ? products : []).filter((row) => number(row.revenue) > 0 || number(row.purchases) > 0).slice(0, 10), [products]);
    const productSeries = productRows.map((row) => ({
        name: String(labelFor(row, ['name', 'product'])),
        data: [{ x: number(row.purchases), y: number(row.revenue), z: Math.max(number(row.purchases) ? number(row.revenue) / number(row.purchases) : 0, 1) }],
    }));
    const bubbleOptions = useMemo(() => ({
        ...base,
        chart: { ...base.chart, type: 'bubble' },
        xaxis: { title: { text: 'Items purchased' }, min: 0, labels: { formatter: formatNumber } },
        yaxis: { title: { text: 'Revenue' }, min: 0, labels: { formatter: formatCurrency } },
        fill: { type: 'solid', opacity: 1 },
        tooltip: { ...base.tooltip, x: { formatter: (value) => `${formatNumber(value)} purchased` }, y: { formatter: formatCurrency }, z: { formatter: (value) => `${formatCurrency(value)} revenue per item` } },
    }), [base]);

    return (
        <InsightGrid>
            <ShareChart title="Category Revenue Mix" subtitle="Revenue concentration across leading product categories" data={mix} loading={loading} cardSx={cardSx} valueFormat="currency" />
            <Card variant="outlined" sx={cardSx}>
                <CardHeader titleTypographyProps={{ component: 'h2' }} title="Product Value Map" subheader="Purchase quantity, revenue, and revenue per item in one comparison" />
                <CardContent>
                    {loading ? <Skeleton variant="rounded" height={280} /> : productSeries.length ? (
                        <Box role="img" aria-label={`Product value map. ${productRows.map((row) => `${labelFor(row, ['name', 'product'])}: ${formatNumber(row.purchases)} purchased and ${formatCurrency(row.revenue)} revenue`).join(', ')}.`}>
                            <ReactApexChart options={bubbleOptions} series={productSeries} type="bubble" height={280} />
                        </Box>
                    ) : <EmptyChart message="No product value data is available for this range." />}
                </CardContent>
            </Card>
        </InsightGrid>
    );
};
